/**
 * DreamScanSection: team-level review-scan controls (TRA-964).
 *
 * Moved from the staff-only Employees page (TRA-942): customers own their
 * nightly cap (empty = shipped default; 0 pauses the team) and the scan
 * rules on the five client-metadata fields. Since TRA-1062 a rule is
 * exclude (skip matches, the original behavior) or include (only scan
 * matches), with exact or regex matching, so "only judge real customer
 * traffic" is expressible. Rules only stop FUTURE judging: traces keep
 * being collected, and flags a newly filtered-out tenant already has in
 * the review queue stay reviewable. Since TRA-1078 a team also picks its
 * run days (see DreamScanForm).
 *
 * Under staff impersonation the form renders disabled: the API refuses
 * impersonated writes, and the rules preview is a POST too. TrainLoop
 * staff also see the run picking switch (TRA-1511).
 *
 * The failure text only replaces the form while there is nothing to show:
 * a refetch that fails behind loaded settings keeps the form, and the
 * draft in it, mounted.
 */

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock } from '@/components/settings/SettingsSection'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useDreamScanSettings } from '@/hooks/useTeamSettings'

import { DreamScanForm } from './DreamScanForm'

export function DreamScanSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { impersonatingTeamId, isEmployee } = useTeam()
  const settingsQuery = useDreamScanSettings()

  return (
    <div>
      <SettingsPageHeader section={section} />

      {settingsQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : settingsQuery.data === undefined && settingsQuery.isError ? (
        <SettingsBlock>
          <p className="text-sm text-muted-foreground">
            {getApiErrorStatus(settingsQuery.error) === 404
              ? 'The nightly scan does not visit this team yet, so there is nothing to configure.'
              : (getApiErrorDetail(settingsQuery.error) ?? 'Failed to load review-scan settings.')}
          </p>
        </SettingsBlock>
      ) : settingsQuery.data ? (
        <DreamScanForm
          key={settingsQuery.data.team_id}
          settings={settingsQuery.data}
          readOnly={impersonatingTeamId !== null}
          showSelection={isEmployee}
        />
      ) : null}
    </div>
  )
}
